import { useState, type FormEvent } from 'react';
import { Owl } from '@/landing/Owl';
import { Button } from '@/components/arc/button/button';
import { Textarea } from '@/components/arc/textarea/textarea';
import { Alert } from '@/components/arc/alert/alert';
import { ApiClient, isApiError } from '@/api/client';
import styles from './sign-in.module.css';

export interface Me {
  org_id: string;
  user_id: string | null;
  is_admin: boolean;
}

/** Production sign-in: paste a session token issued by an operator. */
export function SignIn({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await new ApiClient({ baseUrl: '/api/v1' }).request<Me>('POST', '/auth/session', {
        body: { token: token.trim() },
      });
      onSignedIn(me);
    } catch (err) {
      setError(
        isApiError(err) && err.status === 401
          ? 'That token is invalid or has expired.'
          : 'The API could not be reached.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className={styles.root}>
      <form onSubmit={submit} className={styles.card} aria-labelledby="signin-title">
        <div className={styles.brand}>
          <span className={styles.mark}>
            <Owl className={styles.owl} />
          </span>
          <span id="signin-title" className={styles.name}>
            Scenewise
          </span>
        </div>
        <p className={styles.lead}>
          Open the sign-in link you received, or paste a session token to continue.
        </p>
        <Textarea
          label="Session token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          rows={4}
          placeholder="eyJhbGciOiJIUzI1NiIs…"
          autoFocus
        />
        {error ? <Alert tone="danger" title={error} /> : null}
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={token.trim().length < 16}>
          Sign in
        </Button>
      </form>
    </main>
  );
}
